<template>
  <div class="py-6 container-fluid">
    <!-- Header Section -->
    <div class="d-flex justify-content-between align-items-center mb-4 fade-in">
      <div>
        <h3 class="font-serif text-dark mb-1">Product Inventory</h3>
        <p
          class="text-xs text-muted font-weight-bold text-uppercase tracking-wide"
        >
          Manage your collection
        </p>
      </div>
      <div class="d-flex gap-2">
        <button
          class="btn btn-outline-dark btn-sm text-uppercase tracking-wider px-3"
          @click="openImportModal"
          v-if="can('products.create')"
        >
          <i class="fas fa-file-import me-2"></i> 匯入
        </button>
        <button
          class="btn btn-outline-dark btn-sm text-uppercase tracking-wider px-3"
          @click="exportProducts"
          v-if="can('products.view')"
        >
          <i class="fas fa-file-export me-2"></i> 匯出
        </button>
        <button
          class="btn btn-outline-info btn-sm text-uppercase tracking-wider px-3"
          @click="openSortModal"
          v-if="can('products.edit')"
        >
          <i class="fas fa-sort me-2"></i> 全站排序
        </button>
        <button
          class="btn btn-dark btn-sm text-uppercase tracking-wider px-4"
          @click="$router.push('/products/new')"
          v-if="can('products.create')"
        >
          <i class="fas fa-plus me-2"></i> Add Product
        </button>
      </div>
    </div>

    <!-- Filters -->
    <div class="card border-0 shadow-sm mb-4 fade-in delay-1">
      <div class="card-body p-4">
        <div class="row align-items-end g-3">
          <div class="col-md-3">
            <label
              class="form-label text-xs text-uppercase font-weight-bold text-muted"
              >Search</label
            >
            <div class="input-group">
              <span class="input-group-text border-end-0 bg-white"
                ><i class="fas fa-search text-muted"></i
              ></span>
              <input
                v-model="filters.product_number"
                class="form-control border-start-0 ps-0"
                placeholder="Product Name / SKU"
                @keyup.enter="fetchProducts(1)"
              />
            </div>
          </div>
          <div class="col-md-2">
            <label
              class="form-label text-xs text-uppercase font-weight-bold text-muted"
              >Brand</label
            >
            <select
              class="form-select"
              v-model="filters.brand_id"
              @change="fetchProducts(1)"
            >
              <option value="">All Brands</option>
              <option v-for="b in brands" :key="b.id" :value="b.id">
                {{ b.name }}
              </option>
            </select>
          </div>
          <div class="col-md-2">
            <label
              class="form-label text-xs text-uppercase font-weight-bold text-muted"
              >Category</label
            >
            <select
              class="form-select"
              v-model="filters.category_id"
              @change="fetchProducts(1)"
            >
              <option value="">All Categories</option>
              <option v-for="c in categories" :key="c.id" :value="c.id">
                {{ c.name }}
              </option>
            </select>
          </div>
          <div class="col-md-2">
            <label
              class="form-label text-xs text-uppercase font-weight-bold text-muted"
              >Status</label
            >
            <select
              class="form-select"
              v-model="filters.status"
              @change="fetchProducts(1)"
            >
              <option value="">All Status</option>
              <option value="active">Active</option>
              <option value="inactive">Draft/Inactive</option>
              <option value="sold">Sold Out</option>
            </select>
          </div>
          <div class="col-md-3">
            <label
              class="form-label text-xs text-uppercase font-weight-bold text-muted"
              >Seller</label
            >
            <input
              v-model="filters.seller_email"
              class="form-control"
              placeholder="Seller Email"
              @keyup.enter="fetchProducts(1)"
            />
          </div>
          <div class="col-md-12 text-end mt-3">
            <button
              class="btn btn-outline-secondary btn-sm text-uppercase px-4 me-2"
              @click="resetFilters"
            >
              Reset
            </button>
            <button
              class="btn btn-dark btn-sm text-uppercase px-4"
              @click="fetchProducts(1)"
            >
              Apply Filters
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Product Table -->
    <div class="card border-0 shadow-sm fade-in delay-2">
      <div class="card-body px-0 pt-0 pb-2">
        <!-- Top Pagination -->
        <div
          class="d-flex justify-content-end py-3 px-4 border-bottom"
          v-if="pagination.total > 0"
        >
          <soft-pagination
            color="dark"
            :current="pagination.current_page"
            :total="pagination.last_page"
            @change="fetchProducts"
          />
        </div>

        <div class="table-responsive p-0">
          <table class="table align-items-center mb-0">
            <thead class="bg-gray-50">
              <tr>
                <th class="text-secondary opacity-7 ps-4">Actions</th>
                <th
                  class="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7 ps-2"
                >
                  Product
                </th>
                <th
                  class="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7 ps-2"
                >
                  Category
                </th>
                <th
                  class="text-center text-uppercase text-secondary text-xxs font-weight-bolder opacity-7"
                >
                  Price
                </th>
                <th
                  class="text-center text-uppercase text-secondary text-xxs font-weight-bolder opacity-7"
                >
                  Stock
                </th>
                <th
                  class="text-center text-uppercase text-secondary text-xxs font-weight-bolder opacity-7"
                >
                  Status
                </th>
                <th
                  class="text-center text-uppercase text-secondary text-xxs font-weight-bolder opacity-7"
                >
                  Seller
                </th>
              </tr>
            </thead>
            <tbody>
              <tr v-if="loading">
                <td colspan="7" class="text-center py-5">
                  <div class="spinner-border text-dark" role="status"></div>
                </td>
              </tr>
              <template v-else>
                <tr
                  v-for="product in products"
                  :key="product.id"
                  class="group-hover:bg-gray-50 transition-colors"
                >
                  <td class="align-middle ps-4">
                    <div class="d-flex gap-2">
                      <!-- Quick Actions -->
                      <button
                        class="btn btn-link text-secondary p-0 mb-0"
                        data-bs-toggle="tooltip"
                        title="Duplicate"
                        @click="duplicateProduct(product.id)"
                        v-if="can('products.create')"
                      >
                        <i class="fas fa-copy text-sm"></i>
                      </button>
                      <button
                        class="btn btn-link text-secondary p-0 mb-0"
                        data-bs-toggle="tooltip"
                        title="Edit"
                        @click="editProduct(product.id)"
                        v-if="can('products.edit')"
                      >
                        <i class="fas fa-edit text-sm"></i>
                      </button>
                      <button
                        class="btn btn-link text-secondary p-0 mb-0"
                        data-bs-toggle="tooltip"
                        title="Adjustments"
                        @click="openAdjustmentModal(product)"
                        v-if="can('products.edit')"
                      >
                        <i class="fas fa-sliders-h text-sm"></i>
                      </button>
                      <button
                        class="btn btn-link text-danger p-0 mb-0"
                        data-bs-toggle="tooltip"
                        title="Delete"
                        @click="deleteProduct(product.id)"
                        v-if="can('products.delete')"
                      >
                        <i class="fas fa-trash text-sm"></i>
                      </button>
                    </div>
                  </td>
                  <td>
                    <div class="d-flex py-1">
                      <div>
                        <img
                          :src="product.image_url || '/no-image.png'"
                          class="avatar avatar-lg me-3 border-radius-sm object-cover shadow-sm bg-white"
                          alt="product"
                        />
                      </div>
                      <div class="d-flex flex-column justify-content-center">
                        <h6 class="mb-1 text-sm font-serif text-dark">
                          {{ product.name }}
                        </h6>
                        <p class="text-xs text-secondary mb-0 font-monospace">
                          {{ product.product_code || product.sku || "N/A" }}
                        </p>
                        <span
                          v-if="product.is_violation"
                          class="badge bg-danger mt-1 d-inline-block w-auto"
                          >Violation</span
                        >
                      </div>
                    </div>
                  </td>
                  <td>
                    <p
                      class="text-xs font-weight-bold mb-0 text-uppercase tracking-wide text-secondary"
                    >
                      {{ product.brand?.name }}
                    </p>
                    <p class="text-xs text-secondary mb-0">
                      {{ product.product_type }}
                    </p>
                  </td>
                  <td class="align-middle text-center">
                    <span
                      class="text-secondary text-sm font-weight-bold font-serif"
                      >NT$ {{ Number(product.price).toLocaleString() }}</span
                    >
                  </td>
                  <td class="align-middle text-center">
                    <span
                      class="text-xs font-weight-bold"
                      :class="
                        product.stock > 0 ? 'text-success' : 'text-danger'
                      "
                    >
                      {{
                        product.stock > 0
                          ? `${product.stock} in stock`
                          : "Out of Stock"
                      }}
                    </span>
                  </td>
                  <td class="align-middle text-center text-sm">
                    <span
                      class="badge badge-sm border cursor-pointer hover-scale"
                      :class="getStatusBadgeClass(product.status)"
                      @click="toggleStatus(product)"
                      title="Click to toggle status"
                    >
                      {{ product.status }}
                    </span>
                  </td>
                  <td class="align-middle text-center">
                    <p
                      class="text-xs font-weight-bold mb-0 cursor-pointer hover-link"
                      @click="showSellerInfo(product.seller)"
                    >
                      {{ product.seller?.name || "Unknown" }}
                    </p>
                  </td>
                </tr>
              </template>
            </tbody>
          </table>
          <div
            v-if="!loading && products.length === 0"
            class="text-center py-5"
          >
            <i class="fas fa-box-open fa-3x text-gray-300 mb-3"></i>
            <p class="text-muted">No products found.</p>
          </div>
        </div>

        <!-- Bottom Pagination -->
        <div
          class="d-flex justify-content-center py-4 border-top"
          v-if="pagination.total > 0"
        >
          <soft-pagination
            color="dark"
            :current="pagination.current_page"
            :total="pagination.last_page"
            @change="fetchProducts"
          />
        </div>
      </div>
    </div>

    <!-- Modals (Adjustment, QR) -->
    <div
      class="modal fade"
      id="adjustmentModal"
      tabindex="-1"
      aria-hidden="true"
    >
      <div class="modal-dialog modal-lg modal-dialog-centered modal-dialog-scrollable">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">管理: {{ selectedProduct?.name }}</h5>
            <button
              type="button"
              class="btn-close"
              data-bs-dismiss="modal"
              aria-label="Close"
            ></button>
          </div>
          <div class="modal-body p-0">
            <!-- Tabs -->
            <ul class="nav nav-tabs nav-justified mb-3 bg-light">
              <li class="nav-item">
                <button
                  class="nav-link rounded-0 border-0 border-bottom border-3"
                  :class="
                    activeTab === 'stock'
                      ? 'active border-primary text-primary fw-bold'
                      : 'text-secondary border-transparent'
                  "
                  @click="activeTab = 'stock'"
                >
                  快速庫存
                </button>
              </li>
              <li class="nav-item">
                <button
                  class="nav-link rounded-0 border-0 border-bottom border-3"
                  :class="
                    activeTab === 'orders'
                      ? 'active border-primary text-primary fw-bold'
                      : 'text-secondary border-transparent'
                  "
                  @click="fetchRelatedOrders"
                >
                  相關訂單
                </button>
              </li>
              <li class="nav-item" v-if="can('warehouses.manage')">
                <button
                  class="nav-link rounded-0 border-0 border-bottom border-3"
                  :class="
                    activeTab === 'warehouse'
                      ? 'active border-primary text-primary fw-bold'
                      : 'text-secondary border-transparent'
                  "
                  @click="fetchWarehouseAllocations"
                >
                  <i class="fas fa-warehouse me-1"></i> 門市分配
                </button>
              </li>
            </ul>

            <!-- Tab Content -->
            <div class="p-3">
              <!-- Tab 1: Stock Adjustment -->
              <div v-if="activeTab === 'stock'">
                <!-- Status (Common) -->
                <div class="form-group mb-4">
                  <label
                    class="form-label text-xs uppercase font-weight-bold text-muted"
                    >狀態</label
                  >
                  <select class="form-select" v-model="adjustmentForm.status">
                    <option value="active">上架 (Active)</option>
                    <option value="inactive">下架 (Inactive)</option>
                    <option value="sold">售完 (Sold Out)</option>
                  </select>
                </div>

                <!-- Variants Mode -->
                <div
                  v-if="
                    adjustmentForm.variants &&
                    adjustmentForm.variants.length > 0
                  "
                >
                  <label
                    class="form-label text-xs uppercase font-weight-bold text-muted mb-3"
                    >變體庫存與價格</label
                  >
                  <div
                    class="table-responsive"
                    style="max-height: 300px; overflow-y: auto"
                  >
                    <table class="table table-sm align-middle">
                      <thead class="bg-light sticky-top">
                        <tr>
                          <th class="text-xs text-secondary">變體</th>
                          <th
                            class="text-xs text-secondary text-center"
                            style="width: 100px"
                          >
                            價格
                          </th>
                          <th
                            class="text-xs text-secondary text-center"
                            style="width: 80px"
                          >
                            預留
                          </th>
                          <th
                            class="text-xs text-secondary text-center"
                            style="width: 80px"
                          >
                            可用
                          </th>
                          <th
                            class="text-xs text-secondary text-center"
                            style="width: 80px"
                          >
                            庫存
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr
                          v-for="(variant, idx) in adjustmentForm.variants"
                          :key="idx"
                        >
                          <td>
                            <div class="d-flex flex-column">
                              <span
                                class="text-sm font-weight-bold text-dark"
                                >{{ getVariantLabel(variant) }}</span
                              >
                              <span class="text-xxs text-secondary">{{
                                variant.variant_code
                              }}</span>
                            </div>
                          </td>
                          <td>
                            <input
                              type="number"
                              class="form-control form-control-sm text-center"
                              v-model="variant.price"
                            />
                          </td>
                          <td class="text-center align-middle">
                            <input
                              type="number"
                              class="form-control form-control-sm text-center"
                              v-model="variant.reserved_stock"
                            />
                          </td>
                          <td class="text-center align-middle">
                             <span class="text-xs font-weight-bold" :class="(variant.stock || 0) - (variant.reserved_stock || 0) > 0 ? 'text-success' : 'text-danger'">
                                {{ (variant.stock || 0) - (variant.reserved_stock || 0) }}
                             </span>
                          </td>
                          <td>
                            <input
                              type="number"
                              class="form-control form-control-sm text-center"
                              v-model="variant.stock"
                            />
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>

                <!-- Simple Product Mode -->
                <div v-else>
                  <div class="row">
                    <div class="col-6">
                      <div class="form-group mb-3">
                         <label class="form-label text-xs uppercase font-weight-bold text-muted">庫存概覽</label>
                         <div class="d-flex gap-3">
                             <div class="border rounded p-2 text-center flex-fill">
                                 <div class="text-xxs text-secondary">預留</div>
                                 <input type="number" class="form-control form-control-sm text-center border-0 p-0 fw-bold" v-model="adjustmentForm.reserved_stock" />
                             </div>
                             <div class="border rounded p-2 text-center flex-fill">
                                 <div class="text-xxs text-secondary">可用</div>
                                 <div class="font-weight-bold p-0" :class="(adjustmentForm.stock || 0) - (adjustmentForm.reserved_stock || 0) > 0 ? 'text-success' : 'text-danger'">
                                     {{ (adjustmentForm.stock || 0) - (adjustmentForm.reserved_stock || 0) }}
                                 </div>
                             </div>
                         </div>
                      </div>
                    </div>
                    <div class="col-6">
                      <div class="form-group mb-3">
                        <label
                          class="form-label text-xs uppercase font-weight-bold text-muted"
                          >價格</label
                        >
                        <input
                          type="number"
                          class="form-control"
                          v-model="adjustmentForm.price"
                        />
                      </div>
                    </div>
                    <div class="col-6">
                      <div class="form-group mb-3">
                        <label
                          class="form-label text-xs uppercase font-weight-bold text-muted"
                          >總庫存</label
                        >
                        <input
                          type="number"
                          class="form-control"
                          v-model="adjustmentForm.stock"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Tab 2: Related Orders -->
              <div v-if="activeTab === 'orders'">
                <div v-if="loadingOrders" class="text-center py-4">
                  <div class="spinner-border text-primary" role="status"></div>
                </div>
                <div
                  v-else-if="relatedOrders.length === 0"
                  class="text-center py-4"
                >
                  <p class="text-muted">此商品無相關訂單。</p>
                </div>
                <div
                  v-else
                  class="table-responsive"
                  style="max-height: 400px; overflow-y: auto"
                >
                  <table class="table table-sm align-middle mb-0">
                    <thead class="bg-light sticky-top">
                      <tr>
                        <th class="text-xs text-secondary ps-3">訂單編號</th>
                        <th class="text-xs text-secondary">買家</th>
                        <th class="text-xs text-secondary text-center">日期</th>
                        <th class="text-xs text-secondary text-center">
                          狀態
                        </th>
                        <th class="text-xs text-secondary text-end pe-3">
                          操作
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="order in relatedOrders" :key="order.id">
                        <td class="ps-3">
                          <span class="text-sm fw-bold text-dark">{{
                            order.order_number
                          }}</span>
                        </td>
                        <td>
                          <div class="d-flex flex-column">
                            <span class="text-xs fw-bold">{{
                              order.shipping_name || order.buyer?.name
                            }}</span>
                            <span class="text-xxs text-secondary">{{
                              order.buyer?.email
                            }}</span>
                          </div>
                        </td>
                        <td class="text-center text-xs text-secondary">
                          {{ new Date(order.created_at).toLocaleDateString() }}
                        </td>
                        <td class="text-center">
                          <span
                            class="badge badge-sm border"
                            :class="getStatusBadgeClass(order.status)"
                          >
                            {{ order.status }}
                          </span>
                        </td>
                        <td class="text-end pe-3">
                          <button
                            class="btn btn-xs btn-outline-dark mb-0"
                            @click="viewOrder(order.id)"
                          >
                            View
                          </button>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>

              <!-- Tab 3: Warehouse Allocation -->
              <div v-if="activeTab === 'warehouse'">
                <div v-if="warehouseLoading" class="text-center py-4">
                  <div class="spinner-border spinner-border-sm text-primary"></div>
                  <div class="text-xs text-muted mt-2">載入中...</div>
                </div>
                <div v-else>
                  <!-- Summary bar -->
                  <div class="d-flex gap-3 mb-3 p-2 bg-light rounded text-sm">
                    <div>總庫存：<strong>{{ selectedProduct?.stock ?? 0 }}</strong></div>
                    <div>已分配：<strong class="text-primary">{{ totalWarehouseAllocated }}</strong></div>
                    <div>未分配：<strong :class="unallocatedStock > 0 ? 'text-success' : 'text-danger'">{{ unallocatedStock }}</strong></div>
                  </div>

                  <!-- Quick assign form -->
                  <div class="border rounded p-2 mb-3 bg-white">
                    <div class="text-xs text-uppercase text-muted fw-bold mb-2">快速指派門市</div>
                    <div class="row g-2 align-items-end">
                      <!-- Variant selector (only if product has variants) -->
                      <div class="col-12" v-if="adjustmentForm.variants && adjustmentForm.variants.length > 0">
                        <label class="form-label text-xs mb-1">規格</label>
                        <select v-model="quickAssign.product_variant_id" class="form-select form-select-sm">
                          <option :value="null">— 選擇規格 —</option>
                          <option v-for="v in adjustmentForm.variants" :key="v.id" :value="v.id">
                            {{ v.variant_code }} · {{ getVariantOptionLabel(v) }} (庫存: {{ v.stock }})
                          </option>
                        </select>
                      </div>
                      <!-- Warehouse selector -->
                      <div class="col-7">
                        <label class="form-label text-xs mb-1">門市 / 倉庫</label>
                        <select v-model="quickAssign.warehouse_id" class="form-select form-select-sm">
                          <option :value="null">— 選擇倉庫 —</option>
                          <option
                            v-for="wh in availableWarehouses"
                            :key="wh.id"
                            :value="wh.id"
                          >
                            {{ wh.name }} ({{ wh.type === 'online' ? '線上' : '門市' }})
                          </option>
                        </select>
                      </div>
                      <!-- Quantity -->
                      <div class="col-3">
                        <label class="form-label text-xs mb-1">數量</label>
                        <input
                          v-model.number="quickAssign.allocated_stock"
                          type="number" min="0"
                          class="form-control form-control-sm text-center"
                          placeholder="0"
                        />
                      </div>
                      <!-- Submit -->
                      <div class="col-2">
                        <button
                          class="btn btn-primary btn-sm w-100"
                          :disabled="!quickAssign.warehouse_id || quickAssign.allocated_stock == null || addingAlloc"
                          @click="addWarehouseAllocation"
                        >
                          <span v-if="addingAlloc"><i class="fas fa-spinner fa-spin"></i></span>
                          <span v-else><i class="fas fa-plus"></i></span>
                        </button>
                      </div>
                    </div>
                    <div v-if="quickAssign.error" class="text-danger text-xs mt-1">{{ quickAssign.error }}</div>
                  </div>

                  <!-- Existing allocations table -->
                  <div v-if="warehouseAllocations.length === 0" class="text-center py-2 text-muted text-sm">
                    尚無門市分配，請用上方表單新增
                  </div>
                  <div v-else>
                    <div v-for="group in warehouseAllocationGroups" :key="group.key" class="mb-2">
                      <div v-if="adjustmentForm.variants && adjustmentForm.variants.length > 0"
                           class="text-xs text-muted fw-bold mb-1">
                        {{ group.variantCode }}
                        <span v-if="group.variantOptionLabel"> · {{ group.variantOptionLabel }}</span>
                        <span class="text-dark">（庫存 {{ group.totalStock }}）</span>
                      </div>
                      <div style="overflow-x: auto">
                        <table class="table table-sm align-middle mb-0" style="min-width: 480px">
                        <thead class="table-light">
                          <tr>
                            <th class="text-xs text-muted fw-bold">倉庫</th>
                            <th class="text-xs text-muted fw-bold text-center">寄賣數量</th>
                            <th class="text-xs text-muted fw-bold text-center">已賣出</th>
                            <th class="text-xs text-muted fw-bold text-center">剩餘在店</th>
                            <th></th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr v-for="alloc in group.allocations" :key="alloc.id">
                            <!-- Warehouse name -->
                            <td class="text-sm">
                              <span class="badge badge-sm me-1"
                                :class="alloc.warehouse_type === 'online' ? 'bg-info' : 'bg-warning text-dark'">
                                {{ alloc.warehouse_type === 'online' ? '線上' : '門市' }}
                              </span>
                              <strong>{{ alloc.warehouse_name }}</strong>
                            </td>
                            <!-- 寄賣數量 -->
                            <td class="text-center" style="width:90px">
                              <input
                                v-if="editingAllocId === alloc.id"
                                v-model.number="editingAllocStock"
                                type="number" min="0"
                                class="form-control form-control-sm text-center"
                                @keyup.esc="editingAllocId = null"
                              />
                              <strong v-else>{{ alloc.allocated_stock }}</strong>
                            </td>
                            <!-- 已賣出 -->
                            <td class="text-center" style="width:90px">
                              <input
                                v-if="editingAllocId === alloc.id"
                                v-model.number="editingSoldQty"
                                type="number" min="0"
                                class="form-control form-control-sm text-center"
                                @keyup.enter="saveAllocation(alloc)"
                                @keyup.esc="editingAllocId = null"
                              />
                              <span v-else class="text-danger fw-semibold">{{ alloc.sold_qty ?? 0 }}</span>
                            </td>
                            <!-- 剩餘在店 -->
                            <td class="text-center" style="width:80px">
                              <span v-if="editingAllocId === alloc.id"
                                class="badge"
                                :class="(editingAllocStock - editingSoldQty) >= 0 ? 'bg-success' : 'bg-danger'">
                                {{ Math.max(0, editingAllocStock - editingSoldQty) }}
                              </span>
                              <span v-else class="badge"
                                :class="(alloc.remaining_in_store ?? alloc.allocated_stock) > 0 ? 'bg-success' : 'bg-secondary'">
                                {{ alloc.remaining_in_store ?? alloc.allocated_stock }}
                              </span>
                            </td>
                            <!-- Actions -->
                            <td class="text-end" style="width:80px">
                              <template v-if="editingAllocId === alloc.id">
                                <button class="btn btn-xs btn-success me-1"
                                  @click="saveAllocation(alloc)" :disabled="savingAlloc">
                                  <i class="fas fa-check"></i>
                                </button>
                                <button class="btn btn-xs btn-outline-secondary"
                                  @click="editingAllocId = null">
                                  <i class="fas fa-times"></i>
                                </button>
                              </template>
                              <template v-else>
                                <button class="btn btn-xs btn-outline-secondary me-1"
                                  @click="startEditAlloc(alloc)">
                                  <i class="fas fa-edit"></i>
                                </button>
                                <button class="btn btn-xs btn-outline-danger"
                                  @click="removeAllocation(alloc)">
                                  <i class="fas fa-trash"></i>
                                </button>
                              </template>
                            </td>
                          </tr>
                        </tbody>
                      </table>
                      </div><!-- /overflow-x: auto -->
                    </div>
                  </div>

                  <div v-if="warehouseAllocError" class="alert alert-danger py-2 text-xs mt-2">
                    {{ warehouseAllocError }}
                  </div>
                </div>
              </div>

            </div>
          </div>
          <div class="modal-footer">
            <button
              type="button"
              class="btn btn-secondary"
              data-bs-dismiss="modal"
            >
              關閉
            </button>
            <button
              type="button"
              class="btn btn-warning me-2"
              v-if="activeTab === 'stock'"
              @click="recalculateStock"
            >
              重新計算庫存 (Recalculate)
            </button>
            <button
              type="button"
              class="btn btn-primary"
              v-if="activeTab === 'stock'"
              @click="saveAdjustment"
            >
              儲存變更
            </button>
          </div>
        </div>
      </div>
    </div>
    <!-- Import Modal -->
    <div class="modal fade" id="importModal" tabindex="-1" aria-hidden="true">
      <div class="modal-dialog modal-dialog-centered">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">Batch Import Products</h5>
            <button
              type="button"
              class="btn-close"
              data-bs-dismiss="modal"
              aria-label="Close"
            ></button>
          </div>
          <div class="modal-body">
            <!-- Step 1: Upload & Verify -->
            <div v-if="!verificationResult">
              <div
                class="alert alert-info text-xs text-white mb-3"
                role="alert"
              >
                <i class="fas fa-info-circle me-1"></i>
                Please verified the file first.
              </div>

              <div class="mb-4 text-center">
                <button
                  class="btn btn-outline-primary btn-sm mb-2"
                  @click="downloadTemplate"
                >
                  <i class="fas fa-download me-2"></i> Download Template
                </button>
                <p class="text-xs text-muted">Supports .xlsx, .xls</p>
              </div>

              <hr class="my-4" />

              <div class="mb-3">
                <label class="form-label font-weight-bold">Select File</label>
                <input
                  type="file"
                  class="form-control"
                  ref="importFile"
                  accept=".xlsx, .xls"
                  @change="handleFileChange"
                />
              </div>
            </div>

            <!-- Step 2: Validation Result -->
            <div v-else>
              <div class="text-center mb-4">
                <div
                  class="avatar avatar-xl bg-gradient-success rounded-circle mb-3 mx-auto flex items-center justify-center"
                >
                  <i class="fas fa-check text-white text-2xl"></i>
                </div>
                <h5>Verification Complete</h5>
                <p class="text-xs text-muted">Ready to import</p>
              </div>

              <div class="row text-center mb-3">
                <div class="col-6 border-end">
                  <h4 class="text-success mb-0">
                    {{ verificationResult.created }}
                  </h4>
                  <span
                    class="text-xs text-uppercase font-weight-bold text-muted"
                    >New Items</span
                  >
                </div>
                <div class="col-6">
                  <h4 class="text-info mb-0">
                    {{ verificationResult.updated }}
                  </h4>
                  <span
                    class="text-xs text-uppercase font-weight-bold text-muted"
                    >Updates</span
                  >
                </div>
              </div>

              <div
                v-if="
                  verificationResult.errors &&
                  verificationResult.errors.length > 0
                "
                class="alert alert-warning text-xs text-white max-h-[150px] overflow-y-auto"
              >
                <strong>Warnings:</strong>
                <ul class="mb-0 ps-3">
                  <li
                    v-for="(err, idx) in verificationResult.errors"
                    :key="idx"
                  >
                    {{ err }}
                  </li>
                </ul>
              </div>
            </div>
          </div>
          <div class="modal-footer">
            <button
              type="button"
              class="btn btn-secondary"
              @click="closeImportModal"
            >
              Cancel
            </button>

            <!-- Verify Button -->
            <button
              v-if="!verificationResult"
              type="button"
              class="btn btn-info"
              :disabled="importing"
              @click="submitVerify"
            >
              <span
                v-if="importing"
                class="spinner-border spinner-border-sm me-1"
              ></span>
              {{ importing ? "Verifying..." : "Verify File" }}
            </button>

            <!-- Confirm Import Button -->
            <button
              v-else
              type="button"
              class="btn btn-success"
              :disabled="importing"
              @click="confirmImport"
            >
              <span
                v-if="importing"
                class="spinner-border spinner-border-sm me-1"
              ></span>
              {{ importing ? "Importing..." : "Confirm Upload" }}
            </button>
          </div>
        </div>
      </div>
    </div>
    <!-- Global Sort Modal -->
    <div
      v-if="showSortModal"
      class="modal fade show d-block"
      style="background: rgba(0, 0, 0, 0.5)"
    >
      <div class="modal-dialog modal-lg modal-dialog-scrollable">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">全站商品排序 Adjustment</h5>
            <button
              type="button"
              class="btn-close"
              @click="closeSortModal"
            ></button>
          </div>
          <div class="modal-body">
            <GenericProductSorter
              :fetch-params="{}"
              save-url="/api/admin/products/reorder"
              @close="closeSortModal"
              @saved="fetchProducts"
            />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
// Logic remains largely same, just updated class names and structure
import GenericProductSorter from "@/components/GenericProductSorter.vue";
import SoftPagination from "@/components/SoftPagination.vue";
import { hasPermission } from "@/utils/permissions";
import request from "@/utils/request";
import { Modal } from "bootstrap";
import Swal from "sweetalert2";

export default {
  name: "Products",
  components: { SoftPagination, GenericProductSorter },
  data() {
    return {
      products: [],
      loading: false,
      filters: {
        product_number: "",
        status: "",
        seller_email: "",
        brand_id: "",
        category_id: "",
      },
      brands: [],
      categories: [],
      pagination: { current_page: 1, last_page: 1, total: 0 },
      activeDropdown: null,
      selectedProduct: null,
      adjustmentForm: {
        price: 0,
        stock: 0,
        status: "active",
      },
      adjustmentModalInstance: null,
      activeTab: "stock",
      relatedOrders: [],
      loadingOrders: false,
      // Warehouse allocation (Tab 3)
      warehouseAllocations: [],
      warehouseLoading: false,
      warehouseAllocError: null,
      editingAllocId: null,
      editingAllocStock: 0,
      editingSoldQty: 0,
      savingAlloc: false,
      allWarehouses: [],
      addingAlloc: false,
      quickAssign: { warehouse_id: null, product_variant_id: null, allocated_stock: 0, error: null },
      currentUser: JSON.parse(localStorage.getItem("user") || "{}"),
      importModalInstance: null,
      importing: false,
      verificationResult: null,
      showSortModal: false,
    };
  },
  computed: {
    totalWarehouseAllocated() {
      return this.warehouseAllocations.reduce((s, a) => s + (a.allocated_stock || 0), 0);
    },
    unallocatedStock() {
      return (this.selectedProduct?.stock ?? 0) - this.totalWarehouseAllocated;
    },
    availableWarehouses() {
      // Show all active warehouses; backend will reject duplicates with a clear error
      return this.allWarehouses.filter(w => w.is_active);
    },
    warehouseAllocationGroups() {
      const groups = {};
      this.warehouseAllocations.forEach((alloc) => {
        const key = alloc.product_variant_id ?? 'base';
        if (!groups[key]) {
          const variant = alloc.product_variant_id
            ? (this.adjustmentForm.variants || []).find(v => v.id === alloc.product_variant_id)
            : null;
          groups[key] = {
            key,
            variantCode: alloc.variant_code,
            variantOptionLabel: variant ? this.getVariantOptionLabel(variant) : '',
            totalStock: variant ? (variant.stock ?? 0) : (this.selectedProduct?.stock ?? 0),
            allocations: [],
          };
        }
        groups[key].allocations.push(alloc);
      });
      return Object.values(groups);
    },
  },
  methods: {
    can(permission) {
      return hasPermission(this.currentUser, permission);
    },
    openSortModal() {
      this.showSortModal = true;
    },
    closeSortModal() {
      this.showSortModal = false;
    },
    getStatusBadgeClass(status) {
      const map = {
        active: "bg-success-subtle text-success border-success-subtle",
        inactive: "bg-secondary-subtle text-secondary border-secondary-subtle",
        sold: "bg-warning-subtle text-warning border-warning-subtle",
        violated: "bg-danger-subtle text-danger border-danger-subtle",
      };
      return map[status] || "bg-light text-dark";
    },
    async fetchProducts(page) {
      // If page is not provided, try to get from route query, else default to 1
      if (!page) {
        page = parseInt(this.$route.query.page) || 1;
      }

      this.loading = true;
      try {
        // Update URL query param (replace to avoid history stack buildup or push?)
        // User asked to "remember" it. Pushing helps back button.
        if (parseInt(this.$route.query.page) !== page) {
          this.$router
            .push({ query: { ...this.$route.query, page } })
            .catch(() => {});
        }

        const response = await request.get("/api/admin/products", {
          params: {
            page,
            per_page: 15,
            search: this.filters.product_number,
            status: this.filters.status,
            seller_email: this.filters.seller_email,
            brand_id: this.filters.brand_id,
            category_id: this.filters.category_id,
          },
        });
        this.products = response.data.data;
        this.pagination = response.data.meta;
      } catch (e) {
        console.error(e);
      } finally {
        this.loading = false;
      }
    },
    async fetchBrands() {
      try {
        const res = await request.get("/api/admin/brands");
        this.brands = res.data.data;
      } catch (e) {
        console.error(e);
      }
    },
    async fetchCategories() {
      try {
        const res = await request.get("/api/admin/categories");
        // Flatten if needed, but if backend returns list by default for index (no tree param) we are good.
        // Based on analysis, default is flat list.
        this.categories = res.data.data;
      } catch (e) {
        console.error(e);
      }
    },
    resetFilters() {
      this.filters = {
        product_number: "",
        status: "",
        seller_email: "",
        brand_id: "",
        category_id: "",
      };
      this.fetchProducts(1);
    },
    toggleDropdown(id) {
      this.activeDropdown = this.activeDropdown === id ? null : id;
    },
    editProduct(id) {
      this.$router.push(`/products/${id}/edit`);
    },
    // ... delete, adjustment, etc methods (kept from original but omitted for brevity in artifact)
    async deleteProduct(id) {
      const result = await Swal.fire({
        title: "Are you sure?",
        text: "This action cannot be undone.",
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#1a1a1a",
        cancelButtonColor: "#d33",
        confirmButtonText: "Yes, delete it!",
      });
      if (result.isConfirmed) {
        await request.delete(`/api/admin/products/${id}`);
        this.fetchProducts(this.pagination.current_page);
        Swal.fire("Deleted!", "Product has been deleted.", "success");
      }
    },
    async openAdjustmentModal(product) {
      try {
        // Fetch fresh data including variants
        const res = await request.get(`/api/admin/products/${product.id}`);
        const fullProduct = res.data.data;

        this.selectedProduct = fullProduct;
        this.adjustmentForm = {
          id: fullProduct.id,
          price: fullProduct.price,
          stock: fullProduct.stock,
          reserved_stock: fullProduct.reserved_stock,
          status: fullProduct.status,
          variants: fullProduct.variants
            ? JSON.parse(JSON.stringify(fullProduct.variants))
            : [],
        };

        // eslint-disable-next-line
        const el = document.getElementById("adjustmentModal");
        if (el) {
          this.adjustmentModalInstance = new Modal(el);
          this.adjustmentModalInstance.show();
        }
      } catch (e) {
        console.error(e);
        Swal.fire("Error", "Failed to fetch product details", "error");
      }
    },
    async fetchWarehouseAllocations() {
      this.activeTab = 'warehouse';
      this.warehouseAllocError = null;
      this.editingAllocId = null;
      this.quickAssign = { warehouse_id: null, product_variant_id: null, allocated_stock: 0, error: null };
      this.warehouseLoading = true;
      try {
        const [allocRes, whRes] = await Promise.all([
          request.get(`/api/admin/products/${this.selectedProduct.id}/warehouse-inventory`),
          request.get('/api/admin/warehouses'),
        ]);
        this.warehouseAllocations = allocRes.data.data || [];
        this.allWarehouses = whRes.data.data || [];
      } catch (e) {
        this.warehouseAllocations = [];
        this.allWarehouses = [];
      } finally {
        this.warehouseLoading = false;
      }
    },
    async addWarehouseAllocation() {
      this.quickAssign.error = null;
      if (!this.quickAssign.warehouse_id) { this.quickAssign.error = '請選擇倉庫'; return; }
      const hasVariants = this.adjustmentForm.variants && this.adjustmentForm.variants.length > 0;
      if (hasVariants && !this.quickAssign.product_variant_id) { this.quickAssign.error = '請選擇規格'; return; }
      this.addingAlloc = true;
      try {
        await request.post(`/api/admin/warehouses/${this.quickAssign.warehouse_id}/inventory`, {
          product_id: this.selectedProduct.id,
          product_variant_id: hasVariants ? this.quickAssign.product_variant_id : null,
          allocated_stock: this.quickAssign.allocated_stock,
        });
        this.quickAssign = { warehouse_id: null, product_variant_id: null, allocated_stock: 0, error: null };
        await this.fetchWarehouseAllocations();
      } catch (e) {
        this.quickAssign.error = e.response?.data?.message || '新增失敗，請確認數量不超過總庫存';
      } finally {
        this.addingAlloc = false;
      }
    },
    startEditAlloc(alloc) {
      this.editingAllocId = alloc.id;
      this.editingAllocStock = alloc.allocated_stock;
      this.editingSoldQty = alloc.sold_qty ?? 0;
      this.warehouseAllocError = null;
    },
    async saveAllocation(alloc) {
      this.savingAlloc = true;
      this.warehouseAllocError = null;
      try {
        await request.put(
          `/api/admin/warehouses/${alloc.warehouse_id}/inventory/${alloc.id}`,
          { allocated_stock: this.editingAllocStock, sold_qty: this.editingSoldQty }
        );
        this.editingAllocId = null;
        await this.fetchWarehouseAllocations();
      } catch (e) {
        this.warehouseAllocError = e.response?.data?.message || '儲存失敗，請確認分配數量不超過總庫存';
      } finally {
        this.savingAlloc = false;
      }
    },
    async removeAllocation(alloc) {
      if (!confirm(`確定移除「${alloc.warehouse_name}」的分配？`)) return;
      try {
        await request.delete(`/api/admin/warehouses/${alloc.warehouse_id}/inventory/${alloc.id}`);
        await this.fetchWarehouseAllocations();
      } catch (e) {
        this.warehouseAllocError = '移除失敗';
      }
    },
    async fetchRelatedOrders() {
      this.activeTab = "orders";
      this.loadingOrders = true;
      try {
        const res = await request.get("/api/admin/orders", {
          params: {
            product_id: this.selectedProduct.id,
            per_page: 50,
          },
        });
        this.relatedOrders = res.data.data;
      } catch (e) {
        console.error(e);
        Swal.fire("Error", "Failed to load related orders", "error");
      } finally {
        this.loadingOrders = false;
      }
    },
    viewOrder(id) {
      if (this.adjustmentModalInstance) this.adjustmentModalInstance.hide();
      this.$router.push(`/orders/${id}`);
    },
    getVariantLabel(variant) {
      if (variant.variant_name) return variant.variant_name;
      // Construct from option values
      if (variant.option_values) {
        return Object.entries(variant.option_values)
          .map(([k, v]) => `${k}: ${v}`)
          .join(", ");
      }
      return variant.variant_code;
    },
    getVariantOptionLabel(variant) {
      // Always use option_values for a meaningful label (ignores variant_name which often stores product name)
      if (variant.option_values) {
        const opts = typeof variant.option_values === 'string'
          ? JSON.parse(variant.option_values)
          : variant.option_values;
        const values = Object.values(opts);
        if (values.length > 0) return values.join(' / ');
      }
      if (variant.size) return variant.size;
      if (variant.color) return variant.color;
      return '';
    },
    async saveAdjustment() {
      try {
        const payload = {
          ...this.adjustmentForm,
        };

        // Ensure numeric
        if (payload.variants) {
          payload.variants.forEach((v) => {
            v.price = Number(v.price);
            v.sale_price = v.sale_price ? Number(v.sale_price) : 0;
            v.stock = Number(v.stock);
            v.reserved_stock = Number(v.reserved_stock);
          });
        }

        await request.put(
          `/api/admin/products/${this.selectedProduct.id}`,
          payload
        );

        // Refresh list
        await this.fetchProducts(this.pagination.current_page);

        // Close modal
        if (this.adjustmentModalInstance) {
          this.adjustmentModalInstance.hide();
        }

        Swal.fire("Success", "Product updated successfully", "success");
      } catch (e) {
        console.error(e);
        Swal.fire("Error", "Failed to update product", "error");
      }
    },
    async duplicateProduct(id) {
      const result = await Swal.fire({
        title: "Duplicate Product?",
        text: "This will create a draft copy of the product.",
        icon: "question",
        showCancelButton: true,
        confirmButtonColor: "#1a1a1a",
        confirmButtonText: "Yes, duplicate it!",
      });

      if (result.isConfirmed) {
        try {
          await request.post(`/api/admin/products/${id}/duplicate`);
          Swal.fire("Success", "Product duplicated successfully", "success");
          this.fetchProducts(1); // Go to first page to see new product
        } catch (e) {
          console.error(e);
          Swal.fire("Error", "Failed to duplicate product", "error");
        }
      }
    },
    async toggleStatus(product) {
      const newStatus = product.status === "active" ? "inactive" : "active";
      try {
        // Optimistic update
        product.status = newStatus;

        await request.put(`/api/admin/products/${product.id}`, {
          status: newStatus,
        });

        // Quiet success or toast optional
      } catch (e) {
        console.error(e);
        // eslint-disable-next-line require-atomic-updates
        product.status = product.status === "active" ? "inactive" : "active"; // Revert
        Swal.fire("Error", "Failed to update status", "error");
      }
    },
    async recalculateStock() {
        if (!this.adjustmentForm.id) return;
        
        try {
            const result = await Swal.fire({
                title: 'Are you sure?',
                text: "This will force update the parent product stock to match the sum of variants.",
                icon: 'warning',
                showCancelButton: true,
                confirmButtonText: 'Yes, recalculate!',
            });

            if (result.isConfirmed) {
                const response = await request.post(`/api/admin/products/${this.adjustmentForm.id}/recalculate-stock`);
                if (response.data.success) {
                    Swal.fire("成功", response.data.message, "success");
                    await this.fetchProducts(this.pagination.current_page);
                    
                    // Refresh the modal data immediately
                    const res = await request.get(`/api/admin/products/${this.adjustmentForm.id}`);
                    const fullProduct = res.data.data;
                    this.selectedProduct = fullProduct;
                    
                    // Update form values
                    this.adjustmentForm.stock = fullProduct.stock;
                    this.adjustmentForm.reserved_stock = fullProduct.reserved_stock;
                    if (fullProduct.variants) {
                        this.adjustmentForm.variants = JSON.parse(JSON.stringify(fullProduct.variants));
                    }
                    
                    // Do NOT close modal so user sees the change
                }
            }
        } catch (e) {
            console.error(e);
            Swal.fire("Error", "Failed to recalculate stock", "error");
        }
    },
    async exportProducts() {
      try {
        const response = await request.get("/api/admin/products/export", {
          responseType: "blob",
        });
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute(
          "download",
          `products_export_${new Date().toISOString().slice(0, 10)}.xlsx`
        );
        document.body.appendChild(link);
        link.click();
        link.remove();
        Swal.fire("Success", "Export downloaded successfully", "success");
      } catch (e) {
        console.error(e);
        Swal.fire("Error", "Failed to export products", "error");
      }
    },
    openImportModal() {
      // eslint-disable-next-line
      const el = document.getElementById("importModal");
      if (el) {
        this.importModalInstance = new Modal(el);
        this.importModalInstance.show();
      }
    },
    closeImportModal() {
      if (this.importModalInstance) {
        this.importModalInstance.hide();
      }
      this.verificationResult = null;
      if (this.$refs.importFile) {
        this.$refs.importFile.value = "";
      }
    },
    async downloadTemplate() {
      try {
        const response = await request.get("/api/admin/products/template", {
          responseType: "blob",
        });
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", "products_import_template.xlsx");
        document.body.appendChild(link);
        link.click();
        link.remove();
      } catch (e) {
        console.error(e);
        Swal.fire("Error", "Failed to download template", "error");
      }
    },
    handleFileChange() {
      this.verificationResult = null;
    },
    async submitVerify() {
      const fileInput = this.$refs.importFile;
      if (!fileInput || !fileInput.files[0]) {
        Swal.fire("Warning", "Please select a file first", "warning");
        return;
      }

      this.importing = true;
      const formData = new FormData();
      formData.append("file", fileInput.files[0]);
      formData.append("dry_run", 1);

      try {
        const res = await request.post("/api/admin/products/import", formData, {
          headers: { "Content-Type": "multipart/form-data" },
        });

        if (res.data.success) {
          this.verificationResult = res.data.data;
        } else {
          Swal.fire(
            "Error",
            res.data.message || "Verification failed",
            "error"
          );
        }
      } catch (e) {
        console.error(e);
        Swal.fire("Error", "Failed to verify file", "error");
      } finally {
        this.importing = false;
      }
    },
    async confirmImport() {
      const fileInput = this.$refs.importFile;
      if (!fileInput || !fileInput.files[0]) {
        Swal.fire("Warning", "Please select a file first", "warning");
        return;
      }

      this.importing = true;
      const formData = new FormData();
      formData.append("file", fileInput.files[0]);
      formData.append("dry_run", 0);

      try {
        const res = await request.post("/api/admin/products/import", formData, {
          headers: { "Content-Type": "multipart/form-data" },
        });

        if (res.data.success) {
          Swal.fire(
            "Success",
            `Imported successfully. Created: ${
              res.data.data?.created || 0
            }, Updated: ${res.data.data?.updated || 0}`,
            "success"
          );
          this.closeImportModal();
          this.fetchProducts(1);
        } else {
          Swal.fire("Error", res.data.message || "Import failed", "error");
        }
      } catch (e) {
        console.error(e);
        Swal.fire("Error", "Failed to import products", "error");
      } finally {
        this.importing = false;
      }
    },
  },
  created() {
    this.fetchBrands();
    this.fetchCategories();
    this.fetchProducts();
  },
};
</script>

<style scoped>
.font-serif {
  font-family: "Noto Serif TC", serif;
}
.tracking-wide {
  letter-spacing: 0.05em;
}
.tracking-wider {
  letter-spacing: 0.1em;
}
.hover-link:hover {
  text-decoration: underline;
  color: #000;
}
.object-cover {
  object-fit: cover;
}

/* Animation Utils */
.fade-in {
  animation: fadeIn 0.5s ease-out forwards;
  opacity: 0;
  transform: translateY(10px);
}
.delay-1 {
  animation-delay: 0.1s;
}
.delay-2 {
  animation-delay: 0.2s;
}
@keyframes fadeIn {
  to {
    opacity: 1;
    transform: translateY(0);
  }
}
</style>
